<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('menu_list')"
      :subtitle="$t('manage_menu_types')"
      icon="mdi-food-variant"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button :disabled="loading.menuTypes" @click="openCreateDialog">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_menu_type") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="search"
      :search-placeholder="$t('search_menu_types_placeholder')"
      searchable
      :loading="loading.menuTypes"
    />

    <MenuTypeDialog
      v-model="dialog"
      :menuTypes="menuTypes"
      :menuType="editedItem"
      :is-edit="!!editedItem.id"
      @menu-type-created="handleMenuTypeCreated"
      @menu-type-updated="handleMenuTypeUpdated"
      @close="handleDialogClose"
    />

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="
            customSearch(
              menuTypes.map((type) => {
                return {
                  ...type,
                  code: type.name,
                  name: $t(type.name) || type.name,
                };
              }),
              headers,
              search
            )
          "
          :loading="loading.menuTypes"
          class="striped-table ordera-box-shadow"
          mobile-breakpoint="0"
          show-expand
          loading-text="Loading menu types..."
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <template v-if="item.children && item.children.length">
              <v-btn
                color="primary"
                dark
                small
                rounded
                @click="expand(true)"
                v-if="!isExpanded"
              >
                {{ $t("show") }}
              </v-btn>
              <v-btn color="primary" dark small rounded @click="expand(false)" v-else>
                {{ $t("hide") }}
              </v-btn>
            </template>
          </template>
          <template v-slot:expanded-item="{ item }">
            <template v-if="item.children">
              <td :colspan="headers.length" class="px-0">
                <v-data-table
                  dense
                  :headers="headers"
                  class="ordera-box-shadow striped-table"
                  :items="item.children || []"
                  hide-default-footer
                  hide-default-header
                >
                  <v-spacer></v-spacer>
                  <template v-slot:item.actions="{ item, index }">
                    <edit-button
                      class="mr-2"
                      @click="editMenuType(item)"
                      :disabled="loading.deleting || loading.updating[item.id]"
                    ></edit-button>
                    <remove-button
                      @click="deleteMenuType(item)"
                      :loading="loading.deleting"
                      :disabled="loading.deleting || loading.updating[item.id]"
                    ></remove-button>
                  </template>
                </v-data-table>
              </td>
            </template>
          </template>
          <template v-slot:item.image="{ item }">
            <span class="image-upload-container">
              <v-progress-circular
                v-if="loading.uploading[item.id]"
                indeterminate
                color="primary"
                size="20"
              ></v-progress-circular>
              <template v-else>
                <input
                  class="file-input"
                  type="file"
                  :id="'upload-' + item.id"
                  @change="upload(item, $event)"
                  :disabled="loading.uploading[item.id]"
                />
                <span class="no-image-icon" v-if="!item.image">
                  <v-icon class="image-icon">mdi-image</v-icon>
                </span>
                <label class="image-label" v-else :for="'upload-' + item.id">
                  <v-img
                    height="40"
                    width="40"
                    :src="
                      conf.apiUrlBase +
                      '/gallery/' +
                      $store.state.restaurant.id +
                      '/medium/' +
                      item.image.name
                    "
                  ></v-img>
                  <a
                    class="remove-image-btn"
                    @click.prevent="removeImage(item)"
                    href=""
                    title="Ukloni sliku"
                    >X</a
                  >
                </label>
              </template>
            </span>
          </template>
          <template v-slot:item.name="{ item }">
            {{ $t(item.name) }} ({{ item.code }})
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters v-if="item.restaurant_id">
              <edit-button
                class="mr-2"
                @click="editMenuType(item)"
                :disabled="loading.deleting || loading.updating[item.id]"
              ></edit-button>
              <remove-button
                @click="deleteMenuType(item)"
                :loading="loading.deleting"
                :disabled="loading.deleting || loading.updating[item.id]"
              ></remove-button>
            </v-row>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="processedMenuTypes"
          :loading="loading.menuTypes"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="3"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            {{ $t(item.code) || item.name }}
          </template>
          <template v-slot:item.actions="{ item }">
            <template v-if="item.restaurant_id">
              <edit-button
                class="mr-2"
                @click="editMenuType(item)"
                :disabled="loading.deleting || loading.updating[item.id]"
              ></edit-button>
              <remove-button
                @click="deleteMenuType(item)"
                :loading="loading.deleting"
                :disabled="loading.deleting || loading.updating[item.id]"
              ></remove-button>
            </template>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn
          color="primary"
          block
          :disabled="loading.menuTypes"
          @click="openCreateDialog"
        >
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_menu_type") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import { defaultMenuType } from "@/models/menuType";
import hasSnackbar from "@/mixins/hasSnackbar";
import menuService from "@/services/menu";
import MenuTypeDialog from "@/components/menus/dialogs/MenuTypeDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import conf from "@/config";

export default {
  mixins: [hasSnackbar],
  components: {
    MenuTypeDialog,
    PageHeader,
    PageToolbar,
    MobileDataCards,
  },

  data: () => ({
    conf,
    dialog: false,
    search: "",
    menuTypes: [],
    editedItem: {},
    defaultItem: defaultMenuType,
    loading: {
      menuTypes: false,
      deleting: false,
      uploading: {},
      updating: {},
    },
  }),

  computed: {
    headers() {
      return [
        { text: "ID", align: "start", value: "id" },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("actions"), value: "actions", sortable: false },
        { text: "", value: "data-table-expand" },
      ];
    },
    mobileHeaders() {
      return [
        { text: "ID", value: "id", cols: 2 },
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("actions"), value: "actions", cols: 4 },
      ];
    },
    processedMenuTypes() {
      return this.customSearch(
        this.menuTypes.map((type) => {
          return {
            ...type,
            code: type.name,
            name: this.$t(type.name) || type.name,
          };
        }),
        this.headers,
        this.search
      );
    },
  },

  created() {
    this.loadMenuTypes();
  },

  methods: {
    // Utility methods
    setLoadingState(type, key, state) {
      if (state) {
        this.$set(this.loading[type], key, true);
      } else {
        this.$delete(this.loading[type], key);
      }
    },

    findMenuTypeById(menuTypeId, menuTypes = this.menuTypes) {
      for (let menuType of menuTypes) {
        if (menuType.id === menuTypeId) {
          return menuType;
        }
        if (menuType.children) {
          const found = this.findMenuTypeById(menuTypeId, menuType.children);
          if (found) return found;
        }
      }
      return null;
    },

    /**
     * Loads all menu types from the API
     */
    loadMenuTypes() {
      this.loading.menuTypes = true;

      menuService
        .getMenuTypes()
        .then((res) => {
          this.menuTypes = res.data;
        })
        .catch((error) => {
          console.error("Error loading menu types:", error);
          this.showErrorSnackbar(this.$t("error_loading_menu_types"));
        })
        .finally(() => {
          this.loading.menuTypes = false;
        });
    },
    // Image operations
    removeImage(item) {
      this.setLoadingState("uploading", item.id, true);

      menuService
        .deleteMenuTypeImage(item.id)
        .then((res) => {
          if (res.data) {
            this.updateMenuTypeProperty(item.id, { image_id: null, image: null });
            this.showSuccessSnackbar(this.$t("image_deleted"));
          }
        })
        .catch((error) => {
          console.error("Error removing image:", error);
          this.showErrorSnackbar(this.$t("error_deleting_image"));
        })
        .finally(() => {
          this.setLoadingState("uploading", item.id, false);
        });
    },

    upload(item, event) {
      if (!event.target.files[0]) return;

      this.setLoadingState("uploading", item.id, true);

      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      formData.append("category_id", item.id);

      menuService
        .uploadMenuTypeImage(formData)
        .then((res) => {
          if (res.data) {
            this.updateMenuTypeProperty(item.id, {
              image_id: res.data.id,
              image: res.data,
            });
            this.showSuccessSnackbar(this.$t("image_uploaded"));
          }
        })
        .catch((error) => {
          console.error("Error uploading image:", error);
          this.showErrorSnackbar(this.$t("error_adding_image"));
        })
        .finally(() => {
          this.setLoadingState("uploading", item.id, false);
          if (event.target) {
            event.target.value = "";
          }
        });
    },

    updateMenuTypeProperty(menuTypeId, updates) {
      const menuType = this.findMenuTypeById(menuTypeId);
      if (menuType) {
        Object.assign(menuType, updates);
      }
    },

    editMenuType(item) {
      // Create a clean copy for editing
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteMenuType(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("confirm_delete_menu"), {
          color: "error",
        })
        .then(() => {
          this.loading.deleting = true;

          menuService
            .deleteMenuType(item.id)
            .then((res) => {
              this.removeMenuTypeFromList(item);
              this.showSuccessSnackbar(this.$t("menu_type_deleted"));
            })
            .catch((error) => {
              console.warn("Error deleting menu type:", error);
              this.showErrorSnackbar(this.$t("error_deleting_menu_type"));
            })
            .finally(() => {
              this.loading.deleting = false;
            });
        })
        .catch((err) => console.warn(err));
    },

    removeMenuTypeFromList(item) {
      if (item.parent_id) {
        // Find parent menu type and remove child
        const parentMenuType = this.menuTypes.find((mt) => mt.id === item.parent_id);
        if (parentMenuType?.children) {
          const childIndex = parentMenuType.children.findIndex(
            (child) => child.id === item.id
          );
          if (childIndex > -1) {
            parentMenuType.children.splice(childIndex, 1);
          }
        }
      } else {
        // Remove from main menu types list
        const menuTypeIndex = this.menuTypes.findIndex((mt) => mt.id === item.id);
        if (menuTypeIndex > -1) {
          this.menuTypes.splice(menuTypeIndex, 1);
        }
      }
    },

    openCreateDialog() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },

    handleMenuTypeCreated(menuType) {
      this.addMenuTypeToList(menuType);
    },

    addMenuTypeToList(menuType) {
      if (menuType.parent_id) {
        // Add to parent's children
        const parentMenuType = this.menuTypes.find((mt) => mt.id === menuType.parent_id);
        if (parentMenuType) {
          if (!parentMenuType.children) {
            this.$set(parentMenuType, "children", []);
          }
          parentMenuType.children.push(menuType);
        }
      } else {
        // Add to main menu types list
        this.menuTypes.push(menuType);
      }
    },

    handleMenuTypeUpdated(menuType) {
      this.updateMenuTypeInList(menuType);
    },

    updateMenuTypeInList(menuType) {
      if (menuType.parent_id) {
        // Update child menu type
        const parentMenuType = this.menuTypes.find((mt) => mt.id === menuType.parent_id);
        if (parentMenuType?.children) {
          const childIndex = parentMenuType.children.findIndex(
            (child) => child.id === menuType.id
          );
          if (childIndex > -1) {
            Object.assign(parentMenuType.children[childIndex], menuType);
          }
        }
      } else {
        // Update main menu type
        const menuTypeIndex = this.menuTypes.findIndex((mt) => mt.id === menuType.id);
        if (menuTypeIndex > -1) {
          // Translate name for display
          menuType.name = this.translate(menuType.name);
          Object.assign(this.menuTypes[menuTypeIndex], menuType);
        }
      }
    },

    handleDialogClose() {
      this.editedItem = {};
    },

    // Legacy methods (keeping for compatibility with existing templates)
    editItem(item) {
      this.editMenuType(item);
    },

    deleteItem(item) {
      this.deleteMenuType(item);
    },
  },
};
</script>
<style scoped>
input[type="file"] {
  display: inline-block;
  width: 40px;
  padding: 30px 0 0 0;
  height: 30px;
  overflow: hidden;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.image-upload-container {
  position: relative;
  display: flex;
}

.file-input {
  cursor: pointer;
  position: relative;
  z-index: 999;
}

.no-image-icon {
  position: absolute;
  left: 5px;
  z-index: 0;
  top: -9px;
}

.image-icon {
  font-size: 51px !important;
}

.image-label {
  position: absolute;
  top: -16px;
  margin: 12px;
}

.remove-image-btn {
  position: absolute;
  top: -8px;
  right: -7px;
  background: #c50b0b;
  color: white;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 15px;
  text-decoration: none;
}

.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
